/**
 * Spintax utilities for text like `{Hi|Hello}`.
 * Reserved delimiters (|, *, /) are allowed inside `{...}` blocks.
 */

export function splitOutsideSpintax(text: string, delimiter: string): string[] {
  const parts: string[] = [];
  let current = "";
  let depth = 0;

  for (const char of text) {
    if (char === "{") {
      depth++;
      current += char;
    } else if (char === "}") {
      depth = Math.max(0, depth - 1);
      current += char;
    } else if (char === delimiter && depth === 0) {
      parts.push(current);
      current = "";
    } else {
      current += char;
    }
  }

  parts.push(current);
  return parts;
}

export function containsDisallowedCharOutsideSpintax(
  text: string,
  disallowedChars: string
): boolean {
  let depth = 0;

  for (const char of text) {
    if (char === "{") {
      depth++;
    } else if (char === "}") {
      depth = Math.max(0, depth - 1);
    } else if (depth === 0 && disallowedChars.includes(char)) {
      return true;
    }
  }

  return false;
}

export function stripDisallowedOutsideSpintax(
  value: string,
  disallowedChars: string
): string {
  let depth = 0;
  let result = "";

  for (const char of value) {
    if (char === "{") {
      depth++;
      result += char;
    } else if (char === "}") {
      depth = Math.max(0, depth - 1);
      result += char;
    } else if (depth === 0 && disallowedChars.includes(char)) {
      continue;
    } else {
      result += char;
    }
  }

  return result;
}

function pickSpintaxOption(options: string[], seed: string): string {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = (hash * 31 + seed.charCodeAt(i)) | 0;
  }
  return options[Math.abs(hash) % options.length];
}

/** Resolve innermost `{a|b}` blocks; stable pick per block content for preview. */
export function evaluateSpintax(text: string): string {
  const match = text.match(/\{[^{}]*\}/);
  if (!match || match.index === undefined) {
    return text;
  }

  const block = match[0];
  const options = block.slice(1, -1).split("|");
  const chosen = pickSpintaxOption(options, block);

  const before = text.slice(0, match.index);
  const after = text.slice(match.index + block.length);
  return evaluateSpintax(before + chosen + after);
}
